import { useMemo, useState, type FC } from "react";
import useTranslation from "@hooks/use_translation.ts";
import SectionCard from "@components/profile/section_card.tsx";
import ProfileSectionModal from "@components/profile/modals/section_modal.tsx";
import { buildChecklistItems } from "@components/profile/profile_checklist_items.ts";
import ResumeBody from "@components/profile/sections/resume.tsx";
import AboutYouBody from "@components/profile/sections/about_you.tsx";
import ContactInfoBody from "@components/profile/sections/contact_info.tsx";
import EmploymentBody from "@components/profile/sections/employment.tsx";
import EducationBody, { isEducationEmpty } from "@components/profile/sections/education.tsx";
import OnlineProfilesBody from "@components/profile/sections/online_profiles.tsx";
import WorkPreferencesBody from "@components/profile/sections/work_preferences.tsx";
import VoluntarySelfIdBody, {
  isVoluntarySelfIdEmpty,
} from "@components/profile/sections/voluntary_self_id.tsx";
import SkillsBody from "@components/profile/sections/skills.tsx";
import type { ProfileData } from "@/queries/use_profile.ts";

type ProfileSectionDef = {
  key: string;
  titleKey: string;
  column: "left" | "right";
  mobileOrder: number;
  Body: FC<{ data: ProfileData }>;
  isEmpty?: (data: ProfileData) => boolean;
};

const MOBILE_ORDER_CLASS: Record<number, string> = {
  1: "order-1",
  2: "order-2",
  3: "order-3",
  4: "order-4",
  5: "order-5",
  6: "order-6",
  7: "order-7",
  8: "order-8",
  9: "order-9",
};

// The profile page's sections in desktop column order.
const SECTIONS: ProfileSectionDef[] = [
  {
    key: "about_you",
    titleKey: "about_you.title",
    column: "left",
    mobileOrder: 3,
    Body: AboutYouBody,
    isEmpty: (data) => !data.bio?.trim(),
  },
  {
    key: "employment",
    titleKey: "employment.title",
    column: "left",
    mobileOrder: 4,
    Body: EmploymentBody,
  },
  {
    key: "education",
    titleKey: "education.title",
    column: "left",
    mobileOrder: 5,
    Body: EducationBody,
    isEmpty: isEducationEmpty,
  },
  {
    key: "skills",
    titleKey: "skills.title",
    column: "left",
    mobileOrder: 6,
    Body: SkillsBody,
    isEmpty: (data) => (data.skills?.length ?? 0) === 0,
  },
  {
    key: "resume",
    titleKey: "resume.title",
    column: "right",
    mobileOrder: 1,
    Body: ResumeBody,
  },
  {
    key: "contact_info",
    titleKey: "contact_info.title",
    column: "right",
    mobileOrder: 2,
    Body: ContactInfoBody,
  },
  {
    key: "online_profiles",
    titleKey: "online_profiles.title",
    column: "right",
    mobileOrder: 7,
    Body: OnlineProfilesBody,
  },
  {
    key: "work_preferences",
    titleKey: "work_preferences.title",
    column: "right",
    mobileOrder: 8,
    Body: WorkPreferencesBody,
  },
  {
    key: "voluntary_self_id",
    titleKey: "voluntary_self_id.title",
    column: "right",
    mobileOrder: 9,
    Body: VoluntarySelfIdBody,
    isEmpty: isVoluntarySelfIdEmpty,
  },
];

type Props = {
  data: ProfileData;
};

const ProfileSections = ({ data }: Props) => {
  const { t } = useTranslation("user", { keyPrefix: "profile_sections" });
  const [openKey, setOpenKey] = useState<string | null>(null);

  const statuses = useMemo(() => {
    const items = buildChecklistItems(data);
    return new Map(items.map((item) => [item.key, item.status]));
  }, [data]);

  const renderCard = (def: ProfileSectionDef) => {
    const title = t(def.titleKey);
    const isEmpty = def.isEmpty ? def.isEmpty(data) : statuses.get(def.key) === "not_started";

    return (
      <SectionCard
        key={def.key}
        title={title}
        editLabel={t("edit", { section: title.toLowerCase() })}
        onEdit={() => setOpenKey(def.key)}
        isEmpty={isEmpty}
        className={`${MOBILE_ORDER_CLASS[def.mobileOrder] ?? ""} md:order-none`}
      >
        <def.Body data={data} />
      </SectionCard>
    );
  };

  const columnCards = (column: "left" | "right") =>
    SECTIONS.filter((def) => def.column === column).map(renderCard);

  return (
    <>
      <div className="flex w-full flex-col gap-5 md:grid md:grid-cols-[minmax(0,2fr)_minmax(0,1fr)] md:items-start">
        <div className="contents md:flex md:flex-col md:gap-5">{columnCards("left")}</div>
        <div className="contents md:flex md:flex-col md:gap-5">{columnCards("right")}</div>
      </div>
      <ProfileSectionModal
        openKey={openKey}
        onOpenChange={(isOpen) => !isOpen && setOpenKey(null)}
        data={data}
        source="profile"
      />
    </>
  );
};

export default ProfileSections;
